/* ============================================================
   Fulfillment selector — Ship to home / Pickup in store +
   UPS Ground delivery promise.

   Pure delivery-date helpers are exposed on window.FulfillmentDelivery
   (and module.exports under Node) so they can be unit-tested in isolation.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Pure date / delivery helpers ---------- */

  // ISO weekday: 1 = Mon … 7 = Sun
  function isoDow(y, mo, d) {
    var js = new Date(Date.UTC(y, mo - 1, d)).getUTCDay(); // 0 = Sun
    return js === 0 ? 7 : js;
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function dateKey(p) { return p.y + '-' + pad(p.mo) + '-' + pad(p.d); }

  function isWorkingDay(p, workingDays, holidays) {
    if (holidays && holidays.indexOf(dateKey(p)) !== -1) return false;
    return workingDays.indexOf(isoDow(p.y, p.mo, p.d)) !== -1;
  }

  // Advance one calendar day.
  function addCalendarDay(p) {
    var dt = new Date(Date.UTC(p.y, p.mo - 1, p.d + 1));
    return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
  }

  function nextWorkingDay(p, workingDays, holidays) {
    var cur = addCalendarDay(p);
    while (!isWorkingDay(cur, workingDays, holidays)) {
      cur = addCalendarDay(cur);
    }
    return cur;
  }

  // Add N business days to a starting working day (transit time).
  function addBusinessDays(start, n, workingDays, holidays) {
    var cur = start;
    var added = 0;
    while (added < n) {
      cur = addCalendarDay(cur);
      if (isWorkingDay(cur, workingDays, holidays)) added++;
    }
    return cur;
  }

  // Wall-clock components for an instant in a given IANA timezone.
  function partsInZone(instantMs, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var o = {};
    dtf.formatToParts(new Date(instantMs)).forEach(function (part) {
      if (part.type !== 'literal') o[part.type] = parseInt(part.value, 10);
    });
    if (o.hour === 24) o.hour = 0;
    return { y: o.year, mo: o.month, d: o.day, h: o.hour, mi: o.minute, s: o.second };
  }

  // UTC instant (ms) for a given wall time in a timezone.
  function wallTimeToInstantMs(y, mo, d, h, mi, timeZone) {
    var guess = Date.UTC(y, mo - 1, d, h, mi, 0);
    var p = partsInZone(guess, timeZone);
    var asUTC = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
    var offset = asUTC - guess;
    return guess - offset;
  }

  /**
   * Core promise computation.
   * opts: { nowMs, timeZone, cutoffHour, cutoffMinute, transitDays, workingDays:[1..7], holidays:[YYYY-MM-DD] }
   * Returns { shipDate, deliveryDate, shipsToday, cutoffMs, msUntilCutoff }
   * shipDate/deliveryDate are {y,mo,d}.
   */
  function computeDelivery(opts) {
    var tz = opts.timeZone || 'America/New_York';
    var workingDays = opts.workingDays && opts.workingDays.length ? opts.workingDays : [1, 2, 3, 4, 5];
    var holidays = opts.holidays || [];
    var now = partsInZone(opts.nowMs, tz);
    var today = { y: now.y, mo: now.mo, d: now.d };

    var todayCutoffMs = wallTimeToInstantMs(today.y, today.mo, today.d, opts.cutoffHour, opts.cutoffMinute, tz);
    var shipsToday = isWorkingDay(today, workingDays, holidays) && opts.nowMs < todayCutoffMs;

    var shipDate = shipsToday ? today : nextWorkingDay(today, workingDays, holidays);
    var cutoffMs = shipsToday
      ? todayCutoffMs
      : wallTimeToInstantMs(shipDate.y, shipDate.mo, shipDate.d, opts.cutoffHour, opts.cutoffMinute, tz);

    var deliveryDate = addBusinessDays(shipDate, opts.transitDays, workingDays, holidays);

    return {
      shipDate: shipDate,
      deliveryDate: deliveryDate,
      shipsToday: shipsToday,
      cutoffMs: cutoffMs,
      msUntilCutoff: Math.max(0, cutoffMs - opts.nowMs)
    };
  }

  function transitDaysFor(zip, zones) {
    if (!zones || !zones.days) return null;
    var prefix = String(zip).slice(0, 3);
    return zones.days.hasOwnProperty(prefix) ? zones.days[prefix] : (zones.default || null);
  }

  /* ---------- Formatting ---------- */

  var WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  var MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY',
    'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

  function ordinal(n) {
    var s = ['TH', 'ST', 'ND', 'RD'], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function formatDeliveryDate(p) {
    var js = new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay();
    return WEEKDAYS[js] + ', ' + MONTHS[p.mo - 1] + ' ' + ordinal(p.d);
  }

  function formatCountdown(ms) {
    var totalMin = Math.floor(ms / 60000);
    var days = Math.floor(totalMin / 1440);
    var hours = Math.floor((totalMin % 1440) / 60);
    var mins = totalMin % 60;
    if (days > 0) return days + 'd ' + hours + 'h';
    if (hours > 0) return hours + 'h ' + mins + 'm';
    return mins + 'm';
  }

  function parseHolidays(str) {
    if (!str) return [];
    return str.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }
  function parseWorkingDays(str) {
    if (!str) return [1, 2, 3, 4, 5];
    return str.split(',').map(function (s) { return parseInt(s.trim(), 10); }).filter(function (n) { return n >= 1 && n <= 7; });
  }

  var FulfillmentDelivery = {
    isoDow: isoDow,
    isWorkingDay: isWorkingDay,
    nextWorkingDay: nextWorkingDay,
    addBusinessDays: addBusinessDays,
    partsInZone: partsInZone,
    wallTimeToInstantMs: wallTimeToInstantMs,
    computeDelivery: computeDelivery,
    transitDaysFor: transitDaysFor,
    formatDeliveryDate: formatDeliveryDate,
    formatCountdown: formatCountdown,
    formatOrdinal: ordinal,
    parseHolidays: parseHolidays,
    parseWorkingDays: parseWorkingDays
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = FulfillmentDelivery;
  }
  if (typeof window !== 'undefined') {
    window.FulfillmentDelivery = FulfillmentDelivery;
  } else {
    return; // Node test context — skip DOM wiring.
  }

  /* ---------- DOM wiring ---------- */

  var ZIP_KEY = 'hh_delivery_zip';
  var zonesPromise = null;

  function loadZones(url) {
    if (zonesPromise) return zonesPromise;
    zonesPromise = fetch(url).then(function (r) { return r.json(); }).catch(function () { return null; });
    return zonesPromise;
  }

  function getStoredZip() {
    try { return localStorage.getItem(ZIP_KEY); } catch (e) { return null; }
  }
  function setStoredZip(zip) {
    try { localStorage.setItem(ZIP_KEY, zip); } catch (e) {}
  }

  function Widget(root) {
    this.root = root;
    this.data = this.readData();
    this.timer = null;
    this.zip = null;
    this.bind();
    this.init();
  }

  Widget.prototype.readData = function () {
    var script = document.querySelector('[data-fulfillment-data="' + this.root.dataset.productId + '"]');
    if (!script) return {};
    try { return JSON.parse(script.textContent); } catch (e) { return {}; }
  };

  Widget.prototype.q = function (sel) { return this.root.querySelector(sel); };

  Widget.prototype.bind = function () {
    var self = this;

    var form = this.q('[data-zip-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var val = (self.q('[data-zip-input]').value || '').trim();
        if (!/^\d{5}$/.test(val)) {
          self.q('[data-zip-error]').classList.remove('hide');
          return;
        }
        self.q('[data-zip-error]').classList.add('hide');
        setStoredZip(val);
        self.applyZip(val);
      });
    }

    var change = this.q('[data-zip-change]');
    if (change) {
      change.addEventListener('click', function () {
        self.q('[data-zip-entry]').classList.remove('hide');
        self.q('[data-delivery-promise]').classList.add('hide');
        self.q('[data-deliver-to]').classList.add('hide');
        var input = self.q('[data-zip-input]');
        if (input) { input.value = self.zip || ''; input.focus(); }
      });
    }

    document.addEventListener('variant:change', function (e) {
      if (e.detail && e.detail.variant) self.onVariantChange(e.detail.variant.id);
    });
  };

  // Add thousands separators to any 4+ digit run (e.g. "2580 available" -> "2,580 available").
  Widget.prototype.commafyCounts = function () {
    this.root.querySelectorAll('[data-ship-qty-text], [data-pickup-qty-text]').forEach(function (el) {
      el.textContent = el.textContent.replace(/\d{4,}/g, function (m) {
        return Number(m).toLocaleString('en-US');
      });
    });
  };

  Widget.prototype.init = function () {
    this.commafyCounts();
    var stored = getStoredZip();
    if (stored && /^\d{5}$/.test(stored)) {
      this.applyZip(stored);
    }
  };

  Widget.prototype.applyZip = function (zip) {
    var self = this;
    this.zip = zip;
    var zipEl = this.q('[data-deliver-zip]');
    if (zipEl) zipEl.textContent = zip;

    this.q('[data-zip-entry]').classList.add('hide');
    this.q('[data-deliver-to]').classList.remove('hide');

    loadZones(this.root.dataset.zonesUrl).then(function (zones) {
      self.zones = zones;
      self.renderPromise();
    });
  };

  Widget.prototype.renderPromise = function () {
    var promise = this.q('[data-delivery-promise]');
    if (!promise || !this.zip) return;
    var transit = transitDaysFor(this.zip, this.zones);
    if (transit == null) { promise.classList.add('hide'); return; }

    var ds = this.root.dataset;
    var opts = {
      nowMs: Date.now(),
      timeZone: ds.timezone || 'America/New_York',
      cutoffHour: parseInt(ds.cutoffHour, 10) || 0,
      cutoffMinute: parseInt(ds.cutoffMinute, 10) || 0,
      transitDays: transit,
      workingDays: parseWorkingDays(ds.workingDays),
      holidays: parseHolidays(ds.holidays)
    };
    var result = computeDelivery(opts);

    var dateEl = promise.querySelector('[data-delivery-date]');
    if (dateEl) dateEl.textContent = formatDeliveryDate(result.deliveryDate);

    promise.classList.remove('hide');
    this.startCountdown(result.cutoffMs);
  };

  Widget.prototype.startCountdown = function (cutoffMs) {
    var self = this;
    var el = this.root.querySelector('[data-countdown]');
    if (!el) return;
    if (this.timer) clearInterval(this.timer);
    function tick() {
      var remaining = cutoffMs - Date.now();
      if (remaining <= 0) { clearInterval(self.timer); self.renderPromise(); return; }
      el.textContent = formatCountdown(remaining);
    }
    tick();
    this.timer = setInterval(tick, 30000);
  };

  Widget.prototype.onVariantChange = function (variantId) {
    var v = this.data[variantId];
    if (!v) return;

    // Ship-to-home
    var shipOpt = this.root.querySelector('.fulfillment-option[data-method="ship"]');
    var shipAvailable = v.available && (!v.tracked || v.shipQty > 0);
    if (shipOpt) {
      shipOpt.dataset.available = shipAvailable ? 'true' : 'false';
      shipOpt.classList.toggle('is-unavailable', !shipAvailable);
    }
    var qtyText = this.root.querySelector('[data-ship-qty-text]');
    var shipQtyStr = Number(v.shipQty).toLocaleString('en-US');
    if (qtyText && v.tracked) qtyText.textContent = (typeof theme !== 'undefined' && theme.strings && theme.strings.fulfillmentCountAvailable
      ? theme.strings.fulfillmentCountAvailable.replace('[count]', shipQtyStr)
      : shipQtyStr + ' available');
    var shipCount = this.q('[data-ship-count]');
    var shipOos = this.q('[data-ship-oos]');
    if (shipCount) shipCount.classList.toggle('hide', !shipAvailable);
    if (shipOos) shipOos.classList.toggle('hide', shipAvailable);

    // Pickup-in-store
    var pickupOpt = this.root.querySelector('.fulfillment-option[data-method="pickup"]');
    var pickupAvailable = v.pickupQty > 0;
    if (pickupOpt) {
      pickupOpt.dataset.available = pickupAvailable ? 'true' : 'false';
      pickupOpt.classList.toggle('is-unavailable', !pickupAvailable);
    }
    var pickupText = this.root.querySelector('[data-pickup-qty-text]');
    var pickupQtyStr = Number(v.pickupQty).toLocaleString('en-US');
    if (pickupText) pickupText.textContent = (typeof theme !== 'undefined' && theme.strings && theme.strings.fulfillmentCountAvailable
      ? theme.strings.fulfillmentCountAvailable.replace('[count]', pickupQtyStr)
      : pickupQtyStr + ' available');
    var pickupCount = this.q('[data-pickup-count]');
    var pickupOos = this.q('[data-pickup-oos]');
    if (pickupCount) pickupCount.classList.toggle('hide', !pickupAvailable);
    if (pickupOos) pickupOos.classList.toggle('hide', pickupAvailable);
  };

  function initAll() {
    document.querySelectorAll('[data-fulfillment-selector]').forEach(function (root) {
      if (root.__fulfillmentInit) return;
      root.__fulfillmentInit = true;
      new Widget(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init for theme-editor section reloads / quick view.
  document.addEventListener('shopify:section:load', initAll);
})();
